<script setup>
// Get started (Fase 1 do Polaris AI, spec 2026-10-08 §4.2): hero do papel, a checklist dos 6 passos vinda do
// setup_status (cada linha abre a página do passo, a mesma do menu) e a faixa "Como o Polaris AI funciona para
// vendas" com 4 cards ligados às telas. Sem PageHeader: a página é a vitrine do assistente.
import { computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { ChartLine16, Checkmark16, ChevronRight16 } from '@carbon/icons-vue';

import PageLayout from 'dashboard/components-next/polaris/PageLayout.vue';
import { Button } from 'dashboard/components-next/polaris/ui/button';
import {
  SETUP_STEP_KEYS,
  SETUP_STEP_ROUTES,
} from 'dashboard/routes/dashboard/polaris/setupSteps';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();

const assistantId = computed(() => Number(route.params.assistantId));
const assistants = useMapGetter('polarisAssistants/getRecords');
const setupStatus = useMapGetter('polarisSetupStatus/getStatus');
const status = computed(() => setupStatus.value(assistantId.value));

onMounted(() => {
  if (!assistants.value?.length) store.dispatch('polarisAssistants/get');
});
watch(
  assistantId,
  id => {
    if (id) store.dispatch('polarisSetupStatus/fetch', id);
  },
  { immediate: true }
);

const steps = computed(() => {
  const states = Object.fromEntries(
    (status.value?.steps || []).map(step => [step.key, step.state])
  );
  return SETUP_STEP_KEYS.map((key, index) => ({
    key,
    number: index + 1,
    state: states[key] || 'todo',
    title: t(`POLARIS.GET_STARTED.STEPS.${key.toUpperCase()}.TITLE`),
    description: t(
      `POLARIS.GET_STARTED.STEPS.${key.toUpperCase()}.DESCRIPTION`
    ),
  }));
});

const doneCount = computed(
  () => steps.value.filter(step => step.state === 'done').length
);

const cards = computed(() => [
  {
    key: 'engage',
    routeName: 'polaris_assistants_inboxes_index',
  },
  {
    key: 'discover',
    routeName: 'polaris_assistants_documents_index',
  },
  {
    key: 'qualify',
    routeName: 'polaris_assistants_scenarios_index',
  },
  {
    key: 'convert',
    routeName: 'polaris_assistants_scenarios_index',
  },
]);

const goTo = routeName => {
  router.push({
    name: routeName,
    params: {
      accountId: route.params.accountId,
      assistantId: assistantId.value,
    },
  });
};
</script>

<template>
  <PageLayout :show-pagination-footer="false" :show-know-more="false">
    <template #body>
      <div class="flex flex-col gap-8 py-2">
        <div class="flex flex-wrap items-stretch gap-6">
          <div class="flex min-w-64 flex-1 flex-col justify-center gap-4">
            <span
              class="flex size-10 items-center justify-center rounded-polaris-lg bg-polaris-accent text-polaris-primary"
            >
              <ChartLine16 aria-hidden="true" />
            </span>
            <h1
              class="m-0 text-3xl font-semibold leading-tight tracking-tight text-polaris-foreground"
            >
              {{ t('POLARIS.GET_STARTED.HERO_TITLE') }}
            </h1>
            <p class="m-0 max-w-xl text-polaris-muted-foreground">
              {{ t('POLARIS.GET_STARTED.HERO_SUBTITLE') }}
            </p>
            <div class="flex gap-2">
              <Button @click="goTo(SETUP_STEP_ROUTES.playbook)">
                {{ t('POLARIS.GET_STARTED.CTA') }}
              </Button>
            </div>
          </div>
          <section
            class="flex-1 basis-96 rounded-polaris-lg border border-solid border-polaris-border bg-polaris-card p-4"
          >
            <h2 class="m-0 text-base font-semibold text-polaris-foreground">
              {{ t('POLARIS.GET_STARTED.CHECKLIST_TITLE') }}
            </h2>
            <p class="mb-2 mt-1 text-sm text-polaris-muted-foreground">
              {{
                t('POLARIS.GET_STARTED.CHECKLIST_SUBTITLE', {
                  done: doneCount,
                  total: steps.length,
                })
              }}
            </p>
            <button
              v-for="step in steps"
              :key="step.key"
              type="button"
              class="flex w-full items-start gap-3 border-0 border-t border-solid border-polaris-border bg-transparent px-1 py-3 text-start text-polaris-foreground hover:bg-polaris-accent"
              @click="goTo(SETUP_STEP_ROUTES[step.key])"
            >
              <span
                class="flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold"
                :class="
                  step.state === 'done'
                    ? 'bg-polaris-success text-polaris-success-foreground'
                    : step.state === 'current'
                      ? 'bg-polaris-info text-polaris-info-foreground'
                      : 'bg-polaris-muted text-polaris-muted-foreground'
                "
              >
                <Checkmark16 v-if="step.state === 'done'" aria-hidden="true" />
                <template v-else>{{ step.number }}</template>
              </span>
              <span class="flex-1">
                <span class="block font-medium">{{ step.title }}</span>
                <span class="block text-sm text-polaris-muted-foreground">
                  {{ step.description }}
                </span>
              </span>
              <ChevronRight16
                aria-hidden="true"
                class="shrink-0 text-polaris-muted-foreground"
              />
            </button>
          </section>
        </div>
        <h2
          class="m-0 text-xs font-semibold uppercase tracking-wider text-polaris-muted-foreground"
        >
          {{ t('POLARIS.GET_STARTED.HOW_TITLE') }}
        </h2>
        <div class="flex flex-wrap gap-3">
          <button
            v-for="card in cards"
            :key="card.key"
            type="button"
            class="flex min-w-44 flex-1 flex-col gap-1.5 rounded-polaris-lg border border-solid border-polaris-border bg-polaris-card p-4 text-start hover:bg-polaris-accent"
            @click="goTo(card.routeName)"
          >
            <span
              class="text-xs font-semibold uppercase tracking-wider text-polaris-muted-foreground"
            >
              {{
                t(`POLARIS.GET_STARTED.CARDS.${card.key.toUpperCase()}.KICKER`)
              }}
            </span>
            <span class="font-medium text-polaris-foreground">
              {{
                t(`POLARIS.GET_STARTED.CARDS.${card.key.toUpperCase()}.TITLE`)
              }}
            </span>
            <span class="text-sm text-polaris-muted-foreground">
              {{
                t(`POLARIS.GET_STARTED.CARDS.${card.key.toUpperCase()}.TEXT`)
              }}
            </span>
            <span
              class="mt-auto w-fit rounded-polaris-md bg-polaris-info px-2 py-0.5 pt-1 text-xs font-medium text-polaris-info-foreground"
            >
              {{
                t(`POLARIS.GET_STARTED.CARDS.${card.key.toUpperCase()}.LINK`)
              }}
            </span>
          </button>
        </div>
      </div>
    </template>
  </PageLayout>
</template>
